/**
 * BL-75 (D-210) · Touch-first placement — the coordinate pipeline (SPEC §3).
 *
 * Viewport (client) point → board cell using the board canvas rect ÷ (GRID*CELL) — the
 * EXISTING transform-safe formula (the rect already reflects the fixed-stage transform +
 * any Phase-2 board zoom). This is deliberately NOT `clientPointToStage` (that util is for
 * stage-local overlays — the tooltip/panel — not board cells).
 *
 * Touch/pen apply an anti-occlusion offset: the aim point sits ABOVE the contact so the
 * finger never covers the target cell. The offset is a SCREEN-px subtraction on clientY
 * applied BEFORE the screen→logical conversion. Mouse uses no offset (the cursor is precise).
 */
import { GRID_WIDTH, GRID_HEIGHT, CELL_SIZE } from "../core/config.ts";
import { PLACEMENT_TUNING, type PlacementTuning } from "./placementTuning.ts";
import type { Cell } from "./placementTypes.ts";

/** Just the rect fields we read — so a cached `DOMRect` or a plain object both work. */
export interface RectLike {
  left: number;
  top: number;
  width: number;
  height: number;
}

type PointerKind = "mouse" | "touch" | "pen";

/**
 * Effective anti-occlusion offset in SCREEN px: at least `AIM_OFFSET_PX`, but floored to
 * `AIM_OFFSET_MIN_CELLS` on-screen cells so it still clears a finger when zoomed in.
 */
export function effectiveAimOffsetPx(
  rect: RectLike,
  tuning: PlacementTuning = PLACEMENT_TUNING,
): number {
  const onScreenCellPx = rect.width / GRID_WIDTH;
  return Math.max(tuning.AIM_OFFSET_PX, onScreenCellPx * tuning.AIM_OFFSET_MIN_CELLS);
}

/**
 * The anti-occlusion lift in SCREEN px, RAMPED TO 0 as the contact nears the board's
 * BOTTOM edge. A constant upward lift ({@link effectiveAimOffsetPx}) makes the tower
 * visible above the finger, but it also makes the bottom rows UNREACHABLE: to aim at the
 * last row the finger would have to travel below the board, where there is no room on a
 * phone (the board's bottom sits at the screen edge). So within `rampBand` px of the
 * bottom edge the lift eases linearly from full → 0, restoring reach to the last rows
 * (finger directly on them at the very edge) while the rest of the board keeps the full
 * lift. This is the edge-aware Offset-Cursor fix (US 6,411,283; Vogel & Baudisch, *Shift*,
 * CHI 2007) in ramp-to-zero form — no below-finger flip, so no jitter / hysteresis. Only
 * the bottom needs it: the lift is purely vertical (clientX untouched, so columns are
 * unaffected) and an UPWARD lift already keeps the TOP row reachable.
 */
export function edgeAwareAimOffsetPx(
  clientY: number,
  rect: RectLike,
  tuning: PlacementTuning = PLACEMENT_TUNING,
): number {
  const base = effectiveAimOffsetPx(rect, tuning);
  if (base <= 0) return 0;
  const distFromBottomPx = rect.top + rect.height - clientY; // room below the contact
  const rampBand = base * 1.4; // start easing the lift this far above the bottom edge
  if (distFromBottomPx >= rampBand) return base;
  return Math.max(0, (base * distFromBottomPx) / rampBand);
}

/**
 * Convert a client point to a board cell. Returns `null` only when there is no usable rect
 * (missing / zero-width — e.g. before layout); otherwise always returns the snapped cell with
 * an `inBounds` flag (callers treat out-of-bounds as a cancel target, but still read col/row).
 */
export function clientToCell(
  clientX: number,
  clientY: number,
  pointerType: PointerKind,
  rect: RectLike | null,
  tuning: PlacementTuning = PLACEMENT_TUNING,
): Cell | null {
  if (!rect || rect.width <= 0) return null;
  // Edge-aware: full lift mid-board, ramped to 0 near the bottom edge so the last rows
  // stay reachable (a constant lift pushed the bottom ~2-3 rows off the board).
  const offset =
    pointerType === "touch" || pointerType === "pen"
      ? edgeAwareAimOffsetPx(clientY, rect, tuning)
      : 0;
  const scale = rect.width / (GRID_WIDTH * CELL_SIZE); // on-screen px per logical px
  const lx = (clientX - rect.left) / scale;
  const ly = (clientY - rect.top - offset) / scale; // anti-occlusion: aim ABOVE the contact
  const col = Math.floor(lx / CELL_SIZE);
  const row = Math.floor(ly / CELL_SIZE);
  const inBounds =
    col >= 0 && col < GRID_WIDTH && row >= 0 && row < GRID_HEIGHT;
  return { col, row, inBounds };
}
